import { MESSAGING_ENDPOINTS } from "@/constants/api.messaging";
import {
  formatMailboxDate,
  type MailboxEmail,
} from "@/features/messaging/types/mailboxEmail";
import { API_URL, getAuthHeaders } from "@/shared/config";

export type ApiMessage = {
  pk: number;
  building: number;
  sender: number;
  sender_name: string;
  subject: string;
  body: string;
  conversation_id: number | null;
  sent_at: string;
  delete_at: string | null;
  is_deleted: boolean;
  has_been_read: boolean;
};

export type ApiConversationStatus = {
  pk: number;
  building: number;
  name: string;
  position: number;
  is_closed: boolean;
};

export type ApiConversation = {
  pk: number;
  building: number;
  status: ApiConversationStatus | string;
  created_at: string;
  users: number[];
  messages: ApiMessage[];
};

export function conversationStatusIsClosed(
  status: ApiConversationStatus | string | undefined,
): boolean {
  if (!status) return false;
  if (typeof status === "object") return Boolean(status.is_closed);
  return String(status).toLowerCase() === "closed";
}

export function resolveMessageIsRead(isFromMe: boolean, hasBeenRead?: boolean): boolean {
  return isFromMe || Boolean(hasBeenRead);
}

export function isNumericMessageId(id: string): boolean {
  return /^\d+$/.test(id.trim());
}

function parseOkConversationFields(data: unknown): ApiConversation[] {
  if (!data || typeof data !== "object") return [];
  const rec = data as Record<string, unknown>;
  if (rec.status !== "OK" || !Array.isArray(rec.fields)) return [];
  return rec.fields as ApiConversation[];
}

export function mapApiMessageToMailboxEmail(
  msg: ApiMessage,
  conversationId: string,
  currentUserId: number | null,
): MailboxEmail {
  const sentAtMs = Date.parse(msg.sent_at);
  const safeMs = Number.isNaN(sentAtMs) ? Date.now() : sentAtMs;
  const isFromMe = currentUserId != null && msg.sender === currentUserId;

  return {
    id: String(msg.pk),
    conversationId,
    subject: msg.subject,
    fromName: isFromMe ? "Moi" : msg.sender_name,
    fromEmail: "",
    senderRole: isFromMe ? "Envoyé" : "Message",
    body: msg.body,
    preview: msg.body.slice(0, 120),
    sentAt: formatMailboxDate(new Date(safeMs)),
    sentAtMs: safeMs,
    isRead: resolveMessageIsRead(isFromMe, msg.has_been_read),
    isFromMe,
    senderUserId: msg.sender,
  };
}

export function mapApiConversationsToMailboxEmails(
  conversations: ApiConversation[],
  currentUserId: number | null,
): MailboxEmail[] {
  const emails: MailboxEmail[] = [];
  for (const conversation of conversations) {
    const conversationId = String(conversation.pk);
    for (const msg of conversation.messages) {
      emails.push(
        mapApiMessageToMailboxEmail(msg, conversationId, currentUserId),
      );
    }
  }
  return emails;
}

export async function fetchConversations(
  token: string,
  buildingId: number,
  signal?: AbortSignal,
): Promise<ApiConversation[]> {
  const url = `${API_URL}${MESSAGING_ENDPOINTS.CRUD_CONVERSATION(buildingId)}`;
  const res = await fetch(url, { headers: getAuthHeaders(token), signal });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(`MESSAGING_HTTP_${res.status}`);
  }
  return parseOkConversationFields(json);
}

export async function fetchClosedConversations(
  token: string,
  buildingId: number,
  signal?: AbortSignal,
): Promise<ApiConversation[]> {
  const url = `${API_URL}${MESSAGING_ENDPOINTS.CRUD_CONVERSATION(buildingId)}?status=closed`;
  const res = await fetch(url, { headers: getAuthHeaders(token), signal });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(`MESSAGING_HTTP_${res.status}`);
  }
  return parseOkConversationFields(json);
}

export async function sendMessage(
  token: string,
  buildingId: number,
  payload: {
    body: string;
    subject?: string;
    conversationId?: number | string;
    recipientUserId?: number;
  },
): Promise<{ messageId: number; conversationId: number }> {
  const url = `${API_URL}${MESSAGING_ENDPOINTS.CRUD_MESSAGE(buildingId)}`;
  const requestBody: Record<string, unknown> = {
    body: payload.body,
    ...(payload.subject ? { subject: payload.subject } : {}),
    ...(payload.conversationId != null ? { conversationId: payload.conversationId } : {}),
    ...(payload.recipientUserId != null ? { recipientUserId: payload.recipientUserId } : {}),
  };
  const res = await fetch(url, {
    method: "POST",
    headers: { ...getAuthHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify(requestBody),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(`MESSAGING_SEND_HTTP_${res.status}`);
  }
  return {
    messageId: (json as { field?: { pk?: number } })?.field?.pk ?? 0,
    conversationId: (json as { conversationId?: number })?.conversationId ?? 0,
  };
}

export async function markMessageRead(
  token: string,
  buildingId: number,
  messagePk: number,
): Promise<void> {
  const url = `${API_URL}${MESSAGING_ENDPOINTS.MESSAGE_READ(buildingId)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(token),
    body: JSON.stringify({ pk: messagePk }),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || (json && typeof json === "object" && (json as { status?: string }).status !== "OK")) {
    throw new Error(`MESSAGING_READ_HTTP_${res.status}`);
  }
}
